export interface Settings {
  scheme: "light" | "dark" | "auto";
  inlineFilterThreshhold: number;
  pageTransitionAnimationDuration: number;
}

declare global {
  interface DocumentEventMap {
    settings: CustomEvent<{ settings: Settings }>;
  }
}

const DEFAULT: Settings = {
  scheme: "auto",
  pageTransitionAnimationDuration: 150,
  inlineFilterThreshhold: 100,
};

export const settingsNames: { [key in keyof Settings]: string } = {
  scheme: "Color Scheme (Light/Dark)",
  pageTransitionAnimationDuration: "Duration for Page Transition Animations (in ms)",
  inlineFilterThreshhold:
    "Number of records to filter on the local thread before queueing filter on a background thread",
};

export const settingsSelections: { [key in keyof Settings]?: Record<string, string> } = {
  scheme: {
    light: "Light Mode",
    dark: "Dark Mode",
    auto: "Device Default",
  },
};

export const settingsNumericConstraints: {
  [key in keyof Settings as Settings[key] extends number
    ? key
    : never]: Settings[key] extends number ? [min: number, max: number] : never;
} = {
  inlineFilterThreshhold: [10, 1000],
  pageTransitionAnimationDuration: [1, 1000],
};

let settings: Partial<Settings> = JSON.parse(localStorage.getItem("settings") || "{}");

window.addEventListener("storage", (event) => {
  if (event.key === "settings") {
    settings = JSON.parse(event.newValue || "{}");
    dispatchEvent();
  }
});

document.addEventListener("settings", (settings: CustomEvent<{ settings: Settings }>) => {
  document.body.classList.remove("light", "dark");
  if (settings.detail.settings.scheme !== "auto") {
    document.body.classList.add("theme", settings.detail.settings.scheme);
  }

  if (
    settings.detail.settings.pageTransitionAnimationDuration ===
    DEFAULT.pageTransitionAnimationDuration
  ) {
    document.body.style.removeProperty("--duration-navigate");
  } else {
    document.body.style.setProperty(
      "--duration-navigate",
      settings.detail.settings.pageTransitionAnimationDuration.toString() + "ms",
    );
  }
});

dispatchEvent();

export function isSettingNumeric(key: keyof Settings): key is NumericKeys {
  return key in settingsNumericConstraints;
}

type NumericKeys = {
  [K in keyof Settings]: Settings[K] extends number ? K : never;
}[keyof Settings];

export function getSetting<TKey extends NumericKeys>(key: TKey): number;
export function getSetting<TKey extends keyof Settings>(key: TKey): Settings[TKey];
export function getSetting<TKey extends keyof Settings>(key: TKey): Settings[TKey] {
  const value = settings[key] ?? DEFAULT[key];

  if (isSettingNumeric(key)) {
    // For legacy reasons we must support a saved value being out-of-range or null
    if (typeof value !== "number") {
      return DEFAULT[key];
    }

    const range = settingsNumericConstraints[key];
    if (value < range[0]) {
      return <Settings[TKey]>range[0];
    } else if (value > range[1]) {
      return <Settings[TKey]>range[1];
    } else {
      return value;
    }
  }

  return value;
}

export function setSetting<TKey extends keyof Settings>(key: TKey, value: Settings[TKey]): void {
  settings = { ...settings, [key]: value };
  if (settings[key] === DEFAULT[key]) {
    delete settings[key];
  }
  localStorage.setItem("settings", JSON.stringify(settings));
  dispatchEvent();
}

export type SettingsKeyValuePair<TKey extends keyof Settings = keyof Settings> = [
  key: TKey,
  value: Settings[TKey],
];

export function* enumerateSettings(): Iterable<SettingsKeyValuePair> {
  for (const key in DEFAULT) {
    const theKey = <keyof Settings>key;
    yield [theKey, settings[theKey] ?? DEFAULT[theKey]];
  }
}

function dispatchEvent(): void {
  const event = new CustomEvent<{ settings: Settings }>("settings", {
    detail: { settings: { ...DEFAULT, ...settings } },
  });
  document.dispatchEvent(event);
}
